// Types
export type CampaignStage = 'acquisition' | 'reengagement' | 'retargeting' | 'retention'

export type CampaignObjective = 'awareness' | 'traffic' | 'engagement' | 'leads' | 'app_promotion' | 'sales'

export type ConversionType =
    | 'RATE'
    | 'TUTORIAL_COMPLETION'
    | 'CONTACT'
    | 'SCHEDULE'
    | 'SUBMIT_APPLICATION'
    | 'SUBSCRIBE'
    | 'ADD_TO_WISHLIST'
    | 'LEAD'
    | 'COMPLETE_REGISTRATION'
    | 'CONTENT_VIEW'
    | 'SEARCH'
    | 'SERVICE_BOOKING_REQUEST'
    | 'CUSTOMIZE_PRODUCT'
    | 'DONATE'
    | 'START_TRIAL'
    | 'ADD_TO_CART'
    | 'INITIATED_CHECKOUT'
    | 'ADD_PAYMENT_INFO'
    | 'PURCHASE'

export type FacebookEventType = 'ViewContent' | 'AddToCart'

export type ExclusionReason = 'invalidPixel'

export interface FunnelStageConfig {
    id: CampaignStage
    name: string
    color: string
    recommendedMinCreatives: number
    recommendedMaxCreatives: number
}

export interface ConversionOption {
    value: ConversionType
    label: string
    description: string
}

export interface PlatformPlacement {
    id: string
    value: string
    label: string
}

export interface Platform {
    id: string
    label: string
    subPlacements: PlatformPlacement[]
}

export interface BudgetAllocation {
    acquisition: number
    reengagement: number
    retargeting: number
    retention: number
}

export interface AudienceSelections {
    acquisition: unknown[]
    reengagement: unknown[]
    retargeting: unknown[]
    retention: unknown[]
}

export interface Audience {
    funnel_stage: CampaignStage
}

// Constants
export const ALL_STAGES: readonly CampaignStage[] = ['acquisition', 'reengagement', 'retargeting', 'retention'] as const

// Campaign objective to available stages mapping
export const OBJECTIVE_AVAILABLE_STAGES: Record<CampaignObjective, CampaignStage[]> = {
    awareness: ['acquisition'],
    traffic: ['acquisition', 'reengagement', 'retargeting'],
    engagement: ['acquisition', 'reengagement', 'retargeting'],
    leads: ['acquisition', 'reengagement', 'retargeting', 'retention'],
    app_promotion: ['acquisition', 'reengagement', 'retargeting', 'retention'],
    sales: ['acquisition', 'reengagement', 'retargeting', 'retention'],
}

// Optimal budget allocation percentages by campaign objective (from the table)
export const OPTIMAL_BUDGET_ALLOCATION: Record<CampaignObjective, BudgetAllocation> = {
    awareness: {
        acquisition: 100,
        reengagement: 0,
        retargeting: 0,
        retention: 0,
    },
    traffic: {
        acquisition: 70,
        reengagement: 20,
        retargeting: 10,
        retention: 0,
    },
    engagement: {
        acquisition: 60,
        reengagement: 25,
        retargeting: 15,
        retention: 0,
    },
    leads: {
        acquisition: 50,
        reengagement: 20,
        retargeting: 20,
        retention: 10,
    },
    app_promotion: {
        acquisition: 50,
        reengagement: 20,
        retargeting: 20,
        retention: 10,
    },
    sales: {
        acquisition: 40,
        reengagement: 20,
        retargeting: 30,
        retention: 10,
    },
}

export const EXCLUDED_AUDIENCES: Record<ExclusionReason, CampaignStage[]> = {
    invalidPixel: ['retargeting', 'retention'],
}

export const EXCLUSION_REASONS: Record<'invalidPixel', ExclusionReason> = {
    invalidPixel: 'invalidPixel',
}

// Sales funnel stages configuration
export const FUNNEL_STAGES: Record<CampaignStage, FunnelStageConfig> = {
    acquisition: {
        id: 'acquisition',
        name: 'Acquisition',
        color: '#37A0E6',
        recommendedMinCreatives: 3,
        recommendedMaxCreatives: 5,
    },
    reengagement: {
        id: 'reengagement',
        name: 'Reengagement',
        color: '#41D8B3',
        recommendedMinCreatives: 2,
        recommendedMaxCreatives: 4,
    },
    retargeting: {
        id: 'retargeting',
        name: 'Retargeting',
        color: '#D8CE41',
        recommendedMinCreatives: 2,
        recommendedMaxCreatives: 3,
    },
    retention: {
        id: 'retention',
        name: 'Retention',
        color: '#DC402D',
        recommendedMinCreatives: 1,
        recommendedMaxCreatives: 3,
    },
}

export const defaultConversionMappings: Record<CampaignObjective, FacebookEventType> = {
    leads: 'ViewContent',
    traffic: 'ViewContent',
    engagement: 'ViewContent',
    sales: 'AddToCart',
    app_promotion: 'ViewContent',
    awareness: 'ViewContent',
}

// Conversion type options for Leads campaigns
export const LEAD_EVENT_TYPES: readonly ConversionOption[] = [
    { value: 'RATE', label: 'Rate', description: 'When users leave a rating or review a service or product' },
    {
        value: 'TUTORIAL_COMPLETION',
        label: 'Complete Tutorial',
        description: 'When users finish a tutorial or walkthrough',
    },
    { value: 'CONTACT', label: 'Contact', description: 'When users reach out (form, call, chat)' },
    { value: 'SCHEDULE', label: 'Schedule', description: 'When users book an appointment or meeting' },
    { value: 'SUBMIT_APPLICATION', label: 'Submit Application', description: 'When users submit an application form' },
    { value: 'SUBSCRIBE', label: 'Subscribe', description: 'When users subscribe to a service or newsletter' },
    { value: 'ADD_TO_WISHLIST', label: 'Add to Wishlist', description: "When an item is added to a user's wishlist" },
    { value: 'LEAD', label: 'Lead', description: 'General lead submission (sign-up, interest form, etc)' },
    { value: 'COMPLETE_REGISTRATION', label: 'Complete Registration', description: 'When users successfully register' },
    { value: 'CONTENT_VIEW', label: 'View Content', description: 'When users view a piece of content' },
    { value: 'SEARCH', label: 'Search', description: 'When users perform a search' },
    { value: 'SERVICE_BOOKING_REQUEST', label: 'Book Service', description: 'When users request to book a service' },
] as const

// Conversion type options for Sales campaigns
export const SALES_EVENT_TYPES: readonly ConversionOption[] = [
    {
        value: 'CUSTOMIZE_PRODUCT',
        label: 'Customise Product',
        description: 'When users customise or configure a product',
    },
    { value: 'DONATE', label: 'Donate', description: 'When users make a donation' },
    { value: 'START_TRIAL', label: 'Start Trial', description: 'When users begin a free trial' },
    { value: 'ADD_TO_CART', label: 'Add to Cart', description: 'When users add items to a shopping cart' },
    { value: 'INITIATED_CHECKOUT', label: 'Initiate Checkout', description: 'When users start the checkout process' },
    { value: 'ADD_PAYMENT_INFO', label: 'Add Payment Info', description: 'When users add payment info at checkout' },
    { value: 'PURCHASE', label: 'Purchase', description: 'When users complete a purchase' },
    { value: 'CONTENT_VIEW', label: 'View Content', description: 'When users view a product or content page' },
    { value: 'SEARCH', label: 'Search', description: 'When users perform a search' },
] as const

// Default conversion types based on campaign objective
export const DEFAULT_EVENT_TYPES: Record<CampaignObjective, ConversionType | undefined> = {
    leads: 'LEAD',
    sales: 'PURCHASE',
    awareness: undefined,
    traffic: undefined,
    engagement: undefined,
    app_promotion: undefined,
}

// Platform placement options for targeting configuration
export const PLATFORM_PLACEMENT_OPTIONS: readonly Platform[] = [
    {
        id: 'facebook',
        label: 'Facebook',
        subPlacements: [
            { id: 'feed', value: 'feed', label: 'News Feed' },
            { id: 'right_hand_column', value: 'right_hand_column', label: 'Right Column' },
            { id: 'marketplace', value: 'marketplace', label: 'Marketplace' },
            { id: 'story', value: 'story', label: 'Stories' },
            { id: 'search', value: 'search', label: 'Search Results' },
            { id: 'instream_video', value: 'instream_video', label: 'In-Stream Videos' },
            { id: 'facebook_reels', value: 'facebook_reels', label: 'Reels' },
            { id: 'facebook_reels_overlay', value: 'facebook_reels_overlay', label: 'Reels Overlay' },
            { id: 'profile_feed', value: 'profile_feed', label: 'Profile Feed' },
            { id: 'notification', value: 'notification', label: 'Facebook Notification' },
        ],
    },
    {
        id: 'instagram',
        label: 'Instagram',
        subPlacements: [
            { id: 'stream', value: 'stream', label: 'Feed' },
            { id: 'story', value: 'story', label: 'Stories' },
            { id: 'reels', value: 'reels', label: 'Reels' },
            { id: 'profile_feed', value: 'profile_feed', label: 'Profile Feed' },
            { id: 'ig_search', value: 'ig_search', label: 'Instagram Search' },
            { id: 'profile_reels', value: 'profile_reels', label: 'Profile Reels' },
        ],
    },
    {
        id: 'messenger',
        label: 'Messenger',
        subPlacements: [
            { id: 'messenger_home', value: 'messenger_home', label: 'Messenger Inbox' },
            { id: 'story', value: 'story', label: 'Messenger Stories' },
        ],
    },
    {
        id: 'audience_network',
        label: 'Audience Network',
        subPlacements: [
            { id: 'classic', value: 'classic', label: 'Native, Banner and Interstitial' },
            { id: 'rewarded_video', value: 'rewarded_video', label: 'Rewarded Videos' },
        ],
    },
] as const

// Placements Meta rejects. Settings saved before their removal may still contain them.
// Meta accepts Instagram Explore Home only together with Instagram Explore, so it is not offered either.
const UNSUPPORTED_PLATFORM_PLACEMENTS: readonly string[] = [
    'facebook:video_feeds',
    'instagram:explore',
    'instagram:explore_home',
]

export const filterSupportedPlacements = (placements: unknown): string[] =>
    Array.isArray(placements)
        ? placements.filter((placement) => !UNSUPPORTED_PLATFORM_PLACEMENTS.includes(placement))
        : []

/** Shown for settings that only take effect when an audience is created (event type, recency). */
export const AUDIENCE_CREATION_SETTING_NOTE =
    'Applied when this ad set was published and cannot be changed for it. Applies to new audiences only.'
